import { useState, useEffect } from "react";
import { axiosInstance } from "../../App";
import { 
  Calendar, ChevronLeft, ChevronRight, CheckCircle2, XCircle,
  Loader2, BarChart3
} from "lucide-react";

// Mini Attendance Widget for Child Cards
export function ChildAttendanceWidget({ childId, childName }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `/attendance/calendar?child_id=${childId}&month=${month}&year=${year}`
        );
        setData(response.data);
      } catch (error) {
        console.error("Error fetching attendance:", error);
      } finally {
        setLoading(false);
      }
    };
    if (childId) fetchAttendance();
  }, [childId, month, year]);

  const monthNames = ["Jan", "Feb", "Mac", "Apr", "Mei", "Jun", "Jul", "Ogo", "Sep", "Okt", "Nov", "Dis"];

  const goToPrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="bg-gray-50 rounded-xl p-3 mt-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-bold text-gray-700">Kehadiran</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={goToPrevMonth} className="w-6 h-6 flex items-center justify-center rounded bg-white hover:bg-gray-100">
            <ChevronLeft className="w-3 h-3" />
          </button>
          <span className="text-xs font-bold text-gray-600 px-2">{monthNames[month - 1]} {year}</span>
          <button onClick={goToNextMonth} className="w-6 h-6 flex items-center justify-center rounded bg-white hover:bg-gray-100">
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-white rounded-lg p-2 text-center">
          <p className="text-lg font-black text-green-600">{stats.present_count || 0}</p>
          <p className="text-xs text-gray-500">Hadir</p>
        </div>
        <div className="bg-white rounded-lg p-2 text-center">
          <p className="text-lg font-black text-red-500">{stats.absent_count || 0}</p>
          <p className="text-xs text-gray-500">Tidak Hadir</p>
        </div>
        <div className="bg-white rounded-lg p-2 text-center">
          <p className="text-lg font-black text-purple-600">{stats.attendance_rate || 0}%</p>
          <p className="text-xs text-gray-500">Kadar</p>
        </div>
      </div>

      {/* Mini Calendar */}
      <div className="grid grid-cols-7 gap-0.5">
        {["I", "S", "R", "K", "J", "Sa", "A"].map(d => (
          <div key={d} className="text-center text-[10px] font-bold text-gray-400 py-0.5">{d}</div>
        ))}
        
        {/* Empty cells for days before month starts */}
        {Array.from({ 
          length: new Date(year, month - 1, 1).getDay() === 0 ? 6 : new Date(year, month - 1, 1).getDay() - 1 
        }).map((_, i) => (
          <div key={`e-${i}`} className="aspect-square" />
        ))}
        
        {/* Calendar days */}
        {data?.calendar?.map((day, idx) => {
          let bgColor = "bg-gray-100";
          if (!day.is_weekend && day.date) {
            bgColor = day.present ? "bg-green-100" : "bg-red-50";
          }
          
          return (
            <div
              key={idx}
              className={`aspect-square rounded ${bgColor} flex items-center justify-center text-[10px] font-bold ${
                day.present ? "text-green-700" : day.is_weekend ? "text-gray-300" : "text-red-400"
              }`}
            >
              {day.day}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-3 mt-2 pt-2 border-t border-gray-200">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-green-100 rounded" />
          <span className="text-[10px] text-gray-500">Hadir</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-red-50 rounded" />
          <span className="text-[10px] text-gray-500">Tidak Hadir</span>
        </div>
      </div>
    </div>
  );
}

// Attendance Badge for Child List
export function AttendanceBadge({ childId }) {
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const today = new Date();
        const response = await axiosInstance.get(
          `/attendance/calendar?child_id=${childId}&month=${today.getMonth() + 1}&year=${today.getFullYear()}`
        );
        setRate(response.data?.stats?.attendance_rate || 0);
      } catch (error) {
        setRate(null);
      } finally {
        setLoading(false);
      }
    };
    if (childId) fetchRate();
  }, [childId]);

  if (loading) return null;
  if (rate === null) return null;

  const color = rate >= 80 ? "bg-green-100 text-green-700" : 
                rate >= 50 ? "bg-yellow-100 text-yellow-700" : 
                "bg-red-100 text-red-600";

  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${color}`}>
      <BarChart3 className="w-2.5 h-2.5" />
      {rate}%
    </span>
  );
}

export default ChildAttendanceWidget;
